// eslint-disable-next-line no-restricted-imports
import {useEffect, useState} from 'react';
import dynamic from 'next/dynamic';
import mixpanel from 'mixpanel-browser';
import {CreateAccountProps} from './create-account-form';
import {PersonalDetailsProps} from './personal-details-form';
import {ContactDetailsProps} from './contact-details-form';
import {VerifyDetailsProps} from './verify-details-form';
import {
  ContactDetailsForm,
  CreateAccountForm,
  PersonalDetailsForm,
} from './formValidationSchema';
import {useCognito, useCognitoVerification} from '../../hooks/cognito';
import {useAppToast} from '../../hooks/app-toast';
import {useUpdateUserMutation} from '../../graphql/generated';
import {UNSUPPORTED_SMS_COUNTRIES} from '../../utils/country';

const CreateAccount = dynamic<CreateAccountProps>(() =>
  import('./create-account-form').then((mod) => mod.CreateAccount),
);
const PersonalDetails = dynamic<PersonalDetailsProps>(() =>
  import('./personal-details-form').then((mod) => mod.PersonalDetails),
);
const ContactDetails = dynamic<ContactDetailsProps>(() =>
  import('./contact-details-form').then((mod) => mod.ContactDetails),
);
const VerifyDetails = dynamic<VerifyDetailsProps>(() =>
  import('./verify-details-form').then((mod) => mod.VerifyDetails),
);

interface CreateAccountControllerProps {
  trackingNamePrefix?: string;
  onAccountRegistration?: () => void;
  redirectAfterVerification?: string;
  referrerCode?: string;
  maxSteps?: number;
  logInUrl?: string;
}

// eslint-disable-next-line no-shadow
enum FORMS {
  CREATE_ACCOUNT = 'create-account',
  PERSONAL_DETAILS = 'personal-details',
  CONTACT_DETAILS = 'contact-details',
  VERIFY_DETAILS = 'verify-details',
}

export const CreateAccountController: React.FC<
  CreateAccountControllerProps
> = ({
  trackingNamePrefix,
  maxSteps = 3,
  redirectAfterVerification,
  referrerCode,
  logInUrl,
  onAccountRegistration,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formState, setFormState] = useState<FORMS>(FORMS.CREATE_ACCOUNT);
  const [textVerification, setTextVerification] = useState(true);
  const [accountDetails, setAccountDetails] = useState<CreateAccountForm>();
  const [personalDetails, setPersonalDetails] = useState<PersonalDetailsForm>();
  const cognito = useCognito();
  const verification = useCognitoVerification();
  const toast = useAppToast();
  const [updateUserMutation] = useUpdateUserMutation();

  const handleCreateAccountSubmit = (props: CreateAccountForm) => {
    setAccountDetails(props);
    setFormState(FORMS.PERSONAL_DETAILS);
    mixpanel.track(`${trackingNamePrefix} 1/${maxSteps}`);
  };

  const handleContactDetailsSubmit = async ({
    areaCode,
    contactType,
    contactNumber,
  }: ContactDetailsForm) => {
    if (accountDetails) {
      const {givenName, familyName, email, password} = accountDetails;
      setTextVerification(contactType === 'phone');
      setIsLoading(true);

      try {
        await cognito.signUp(
          {
            firstName: givenName,
            lastName: familyName,
            email: email.toLowerCase(),
            password,
            phoneNumber:
              contactType === 'phone'
                ? `${areaCode}${
                    contactNumber && contactNumber.replace(/^0+/, '')
                  }`
                : undefined,
            referrerId: referrerCode,
            redirect: redirectAfterVerification,
          },
          true,
        );
        setIsLoading(false);

        mixpanel.alias(email.toLowerCase());

        mixpanel.track(`${trackingNamePrefix} 2/${maxSteps} - text or email`);

        if (referrerCode) {
          mixpanel.track('Registered with referral code', {
            referrer: referrerCode,
          });
        }

        setFormState(FORMS.VERIFY_DETAILS);
      } catch (e: any) {
        setIsLoading(false);
        if (e.name === 'InvalidPasswordException') {
          toast.showErrorToast(
            'Password must be at least 8 characters long and contain a number',
          );
        } else if (e.name === 'UsernameExistsException') {
          toast.showErrorToast(
            'This email is already in use. Log In to continue!',
          );
        } else if (e.name === 'InvalidLambdaResponseException') {
          toast.showErrorToast(
            'This email address is already in use by Google Sign In',
          );
        } else {
          toast.showErrorToast('Something went wrong. Please try again.');
          throw e;
        }
      }
    }
  };

  const handlePersonalDetailsSubmit = (props: PersonalDetailsForm) => {
    setPersonalDetails(props);
    mixpanel.track(`${trackingNamePrefix} 2/${maxSteps} - Deed details`);
    // if unsupported country skip phone number verification
    if (UNSUPPORTED_SMS_COUNTRIES.includes(props.countryCode)) {
      handleContactDetailsSubmit({});
    } else {
      setFormState(FORMS.CONTACT_DETAILS);
    }
  };

  const handleVerifyDetailsSubmit = async (code: string) => {
    setIsLoading(true);

    if (accountDetails) {
      try {
        await cognito.confirmSmsSignUp(accountDetails.email, code);
      } catch (e: any) {
        setIsLoading(false);

        if (e.name === 'CodeMismatchException') {
          toast.showErrorToast(
            'The code provided does not match our records. Please try again.',
          );
        } else if (e.name === 'ExpiredCodeException') {
          toast.showErrorToast(
            'The code provided has expired. Please try again.',
          );
        } else {
          toast.showErrorToast('Something went wrong. Please try again.');
          throw e;
        }
      }
    }
  };

  const handleResendConfirmation = () => {
    verification.onResendVerificationEmailOrSms(accountDetails?.email);
  };

  // user is auto signed in after confirming via sms
  // once authenticated, update DOB + address then continue onboarding
  useEffect(() => {
    if (cognito.user && personalDetails && accountDetails) {
      const {dateOfBirth, ...rest} = personalDetails;
      updateUserMutation({
        variables: {
          title: accountDetails.title,
          dateOfBirth,
          shippingAddress: {...rest},
        },
      }).then(() => {
        if (onAccountRegistration) {
          onAccountRegistration();
        }
      });
    }
  }, [
    cognito.user,
    updateUserMutation,
    personalDetails,
    accountDetails,
    onAccountRegistration,
  ]);

  switch (formState) {
    case FORMS.CREATE_ACCOUNT:
      return (
        <>
          <CreateAccount
            logInUrl={logInUrl}
            maxStepCount={maxSteps}
            isLoading={isLoading}
            onNextSubmit={handleCreateAccountSubmit}
          />
        </>
      );
    case FORMS.PERSONAL_DETAILS:
      return (
        <>
          <PersonalDetails
            maxStepCount={maxSteps}
            isLoading={isLoading}
            onNextSubmit={handlePersonalDetailsSubmit}
          />
        </>
      );
    case FORMS.CONTACT_DETAILS:
      return (
        <>
          <ContactDetails
            maxStepCount={maxSteps}
            isLoading={isLoading}
            onNextSubmit={handleContactDetailsSubmit}
          />
        </>
      );
    case FORMS.VERIFY_DETAILS:
      return (
        <>
          <VerifyDetails
            maxStepCount={maxSteps}
            isLoading={isLoading}
            textVerification={textVerification}
            onNextSubmit={handleVerifyDetailsSubmit}
            onResend={handleResendConfirmation}
          />
        </>
      );
    default:
      return <></>;
  }
};
